import { Link } from "react-router-dom";
import { SkedMark } from "@/components/SkedMark";
import { UtmBulkChecker } from "@/components/utm/UtmBulkChecker";
import { usePageMeta } from "@/hooks/usePageMeta";
import { buildSkedHref } from "@shared/leadgen/cta-rules";

const HEALTH_CHECK_FOOTER_HREF = buildSkedHref({
  medium: "utm_health_check",
  campaign: "site_footer",
});

export function UtmHealthCheckPage() {
  usePageMeta({
    title: "UTM Health Snapshot — free audit · Traffic Snapshot",
    description:
      "Paste a batch of campaign URLs or upload a CSV. We score every row against a canonical UTM taxonomy. No sign-in. Nothing stored server-side.",
  });
  return (
    <div className="min-h-dvh bg-white text-ink antialiased">
      <SiteNav />

      <section className="mx-auto max-w-[1200px] px-8 py-16 md:py-20">
        <span className="mb-[14px] inline-block rounded-full border border-pink-200 px-[14px] py-[7px] text-[11px] font-bold uppercase tracking-[0.22em] text-pink-600">
          UTM Health Snapshot
        </span>
        <h1 className="mt-4 max-w-[22ch] font-display text-5xl font-[900] leading-[1.05] tracking-[-0.025em] text-ink text-balance md:text-[56px]">
          Find out if your UTMs are{" "}
          <span className="font-serif font-semibold italic tracking-[-0.01em] text-purple-500">
            helping
          </span>{" "}
          or hurting.
        </h1>
        <p className="mt-5 max-w-[42ch] border-l-2 border-purple-200 pl-4 font-serif text-[17px] font-medium leading-[1.4] italic text-purple-700">
          Most teams lose 20 points to casing and source drift alone.
        </p>
        <p className="mt-4 max-w-[52ch] text-sm font-medium leading-[1.5] text-neutral-500">
          First-run average score: 68/100. The fix usually takes 20 minutes.
        </p>
        <p className="mt-6 max-w-[56ch] text-[18px] leading-[1.55] text-neutral-700">
          Paste a batch of campaign URLs, or upload a CSV. We score every row against a canonical
          Sked taxonomy. No sign-in. Nothing stored server-side.
        </p>

        <div className="mt-10">
          <UtmBulkChecker variant="public" />
        </div>
      </section>

      <section
        className="py-24 md:py-28"
        style={{ background: "linear-gradient(180deg, #fff 0%, #F4F2FF 100%)" }}
      >
        <div className="mx-auto max-w-[1200px] px-8">
          <span className="mb-[14px] inline-block rounded-full border border-pink-200 px-[14px] py-[7px] text-[11px] font-bold uppercase tracking-[0.22em] text-pink-600">
            Upgrade path
          </span>
          <h2 className="max-w-[22ch] font-display text-5xl font-[900] leading-[1.05] tracking-[-0.025em] text-ink text-balance md:text-[48px]">
            The same score, from your{" "}
            <span className="font-serif font-semibold italic tracking-[-0.01em] text-purple-500">
              live
            </span>{" "}
            social traffic.
          </h2>
          <p className="mt-[18px] max-w-[56ch] text-[19px] leading-[1.55] text-neutral-700">
            Connect GA4 and the hosted version scores the last 90 days of social sessions. Weighted
            by real traffic. With a reusable taxonomy seeded from whatever this audit just flagged.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-5">
            <Link to="/app" className="ts-cta ts-cta-lg">
              Connect GA4 <span className="ts-cta-arrow">→</span>
            </Link>
            <Link to="/" className="ts-link">
              Back to the landing page <span className="ts-link-arrow">→</span>
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}

function SiteNav() {
  return (
    <nav className="sticky top-0 z-20 border-b border-neutral-950/5 bg-white/85 backdrop-blur">
      <div className="mx-auto flex max-w-[1200px] items-center gap-9 px-8 py-4">
        <Link to="/" className="flex items-center gap-3 text-ink no-underline">
          <SkedMark className="h-10 w-10" aria-hidden="true" />
          <span className="leading-[1.05]">
            <span className="block text-xs font-bold tracking-tight text-neutral-500">
              Sked Social
            </span>
            <span className="block font-display text-lg font-[900] tracking-tight text-ink">
              Traffic Snapshot
            </span>
          </span>
        </Link>
        <div className="flex-1" />
        <Link
          to="/"
          className="hidden text-sm font-medium text-neutral-700 hover:text-purple-700 md:inline"
        >
          About Traffic Snapshot
        </Link>
        <Link to="/app" className="ts-cta">
          Connect GA4 <span className="ts-cta-arrow">→</span>
        </Link>
      </div>
    </nav>
  );
}

function SiteFooter() {
  return (
    <footer className="py-14 pb-20">
      <div className="mx-auto flex max-w-[1200px] flex-wrap items-center gap-6 px-8">
        <Link to="/" className="flex items-center gap-2.5 text-ink no-underline">
          <SkedMark className="size-7" aria-hidden="true" />
          <span className="font-display text-[15px] font-[900] tracking-[-0.01em] text-ink">
            Traffic Snapshot
          </span>
        </Link>
        <span className="text-[13px] text-neutral-500">
          <b className="font-bold text-ink">Traffic Snapshot</b> · a free report from Sked Social ·{" "}
          <i className="font-serif font-medium italic text-purple-700">social-first</i> analytics,
          built for marketers.
        </span>
        <div className="flex-1" />
        <a
          href="https://skedsocial.com/privacy-policy"
          target="_blank"
          rel="noreferrer"
          className="text-[13px] text-neutral-600 no-underline hover:text-purple-700"
        >
          Privacy
        </a>
        <a
          href="https://skedsocial.com/terms-of-service"
          target="_blank"
          rel="noreferrer"
          className="text-[13px] text-neutral-600 no-underline hover:text-purple-700"
        >
          Terms
        </a>
        <a
          href={HEALTH_CHECK_FOOTER_HREF}
          target="_blank"
          rel="noreferrer"
          className="text-[13px] text-neutral-600 no-underline hover:text-purple-700"
        >
          skedsocial.com ↗
        </a>
      </div>
    </footer>
  );
}
